//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { Menu, MenuItem } from '@mui/material';
import { Col, Row } from 'antd';
import Loading from 'components/Loading';
import { filter, find, flatMap, get, groupBy, has, map, orderBy, uniqBy } from 'lodash';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
//  -- Constants ----------------------
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import AssignmentStatusParent from 'components/AssignmentStatusParent';
import moment from 'moment';
import 'moment-timezone';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useApExams } from 'store/apexams/selectors';
import { useCurrentClass, useCurrentClassBook } from 'store/classes/selectors';
import { useExams } from 'store/exams/selectors';
import { handleListUserNotes } from 'store/notes/thunks';
import { useClassGameActivity } from 'store/one_game/selectors';
import { useOrgTimezone, useParentOrgId } from 'store/organizations/selectors';
import { useResources } from 'store/resources/selectors';
import { useServertime } from 'store/servertime/selectors';
import { useStory } from 'store/story/selectors';
import { handleListUserSubmissionsByClass } from 'store/submissions/thunks';
import {
  nothingToSee,
} from 'utils/assets/images';
import tableViewSvg from "../utils/assets/images/tableView.svg";
import COLOR from 'utils/constants/color';
import { useComponentVisibleHook } from 'utils/hooks';
import { filterStudentSubmission, getUserAssignment, isVideo } from 'utils/lib/ebookHelper';
import * as Sentry from '@sentry/react';
import dayjs from 'utils/dayjsConfig';
import {
  checkIsManualGradable,
  filterActiveTasks,
  getCurrenSem,
  isBeforTime,
  semeterDateByYear,
  getSem1EndDate,
} from 'utils/methods/math';
import { createStyledMuiComponent } from 'utils/styles/mui';
import { useUserAllowedBooks } from 'store/books/selectors';
import StudentGradebook from 'modules/StudentDashboardNew/StudentGradebook';
import { handleGetStudentAssignmentCount } from 'store/users/thunks';
import { useCurrentClassActiveBooks } from 'components/hooks/books/useCurrentClassActiveBooks';
import useActiveStudentofParent from 'components/hooks/parent/activeStudentofParent';
import SentryWrap from 'components/Sentry';

const ACTIVE_BTN_COLOR = `linear-gradient(to bottom right, ${COLOR.indigo100} , ${COLOR.indigo300})`
const DEACTIVE_BTN_COLOR = `linear-gradient(to right, ${COLOR.lightseagreen100} , ${COLOR.lightseagreen200})`
const getBgColor = (isActive) => (isActive ? ACTIVE_BTN_COLOR : DEACTIVE_BTN_COLOR);

const chartColors = [
  "url(#pieGradient)",
  "#e9ecf6",
];

const chartConfig = {
  height: 116,
  width: 116,
  innerRadius: '80%',
  outerRadius: '100%',
  titleStyle: {
    fontWeight: "bold",
    fontSize: "24px",
    color: "#000",
  }
};


// const config = {
//   chart: {
//     type: "pie",
//     backgroundColor: "transparent",
//     height: 116,
//     width: 116,
//     spacing: [0, 0, 0, 0],
//   },
//   title: {
//     text: "75%",
//     align: "center",
//     verticalAlign: "middle",
//     y: 10,
//     style: {
//       fontWeight: "bold",
//       fontSize: "24px",
//       color: "#000",
//     },
//   },
//   tooltip: {
//     enabled: false,
//   },
//   plotOptions: {
//     pie: {
//       dataLabels: {
//         enabled: false,
//       },
//       startAngle: -90,
//       endAngle: 270, // Makes it a full circle starting from top
//       center: ["50%", "50%"],
//       size: "100%",
//       borderWidth: 0,
//     },
//   },
//   colors: [
//     {
//       linearGradient: { x1: 0, y1: 1, x2: 1, y2: 0 },
//       stops: [
//         [0, "#ff4ddb"], // Bright pink
//         [1, "#4c65f3"], // Blue
//       ],
//     },
//     "#e9ecf6", // Light grey background
//   ],
//   series: [
//     {
//       type: "pie",
//       innerSize: "80%",
//       data: [{ y: 75 }, { y: 25 }],
//     },
//   ],
// };

const DROPDOWN = [
  { value: "all", label: "All" },
  { value: "due_today", label: "Due Today" },
  { value: "due_this_week", label: "Due This Week" },
  { value: "due_past", label: "Past Due" },
  { value: "completed", label: "Completed" },
  { value: "incompleted", label: "Incomplete" },
  { value: "retakes", label: "Retake Available" },
  { value: "startToday", label: "Start Today" },
  { value: "missing_this_week", label: "Missing This Week" },
];
const config = {
  data: [
    { name: "Overall Grade", value: 75, fill: chartColors[0] },
    { name: "", value: 25, fill: chartColors[1] },
  ],
  title: "75%"
};

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const Days = styled.span`
  color: white;
  display: block;
  font-size: 64px;
  font-weight: 600;
  line-height: 88px;
  text-align: center;

  @media (min-width: 992px) {
    font-size: 80px;
  }
`;
const Header = styled.span`
  color: ${COLOR.black300};
  display: block;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Section = styled.div`
  align-items: center;
  border-radius: 3px;
  box-shadow: 0 0px 15px rgb(0 0 0 / 85%), 0 0px 2px rgb(0 0 0 / 85%);
  flex-direction: row;
  height: ${(p) => `${p.height}px` || "100%"};
  margin-bottom: ${(p) => `${p.marginBottom}px`};
  padding: 16px;
  width: 100%;
`;

const Wrapper = styled.div`
  height: auto;
  margin-bottom: 40px;
  width: 100%;
`;

const SettingIcon = styled.img`
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  margin-top: -20px;
`;
const Box = styled.div`
  -webkit-box-shadow: -2px 0px 7px 0px rgba(163, 150, 150, 0.48);
  -moz-box-shadow: -2px 0px 7px 0px rgba(163, 150, 150, 0.48);
  box-shadow: -2px 0px 7px 0px rgba(163, 150, 150, 0.48);
  height: 260px;
  width: 170px;
  padding-bottom: 20px;
  margin-left: 5px;
  margin-right: 5px;
  cursor: pointer;
  background-image: linear-gradient(
    to bottom,
    ${COLOR.indigo300},
    ${COLOR.indigo600},
    ${COLOR.indigo200},
    ${COLOR.indigo100},
    ${COLOR.indigo500}
  );
`;

const BoxRow = styled.div`
  width: 100%;
  flex-direction: row;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;
const NoteTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-left: 10px;
  color: white;
  padding-top: 10px;
  text-transform: uppercase;
  white-space: pre-line;
  line-height: 20px;
`;
const NoteText = styled.div`
  font-size: 20px;
  text-align: center;
  font-weight: bold;
  color: white;
  margin-left: auto;
  white-space: pre-line;
`;
const ContentWrapper = styled.div`
  height: ${(p) => (p.height ? p.height : "30%")};
`;
const OverallGrade = styled.div`
  height: 150px;
  width: 190px;
  border-radius: 50%;
`;
const OverallGradeSection = styled.div``;
const OverallGradeTitleSec = styled.div`
  background-color: ${COLOR.indigo300};
  min-height: 100px;
  flex-direction: row;
  display: flex;
`;
const OverallGradeTitle = styled.div`
  color: white;
  font-size: 28px;
  font-weight: 300;
  white-space: pre-line;
  margin-left: 60px;
`;
const SearchByTitle = styled(OverallGradeTitle)`
  color: #000;
  font-family: Arial;
  font-size: 17px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  margin: 0px;
  margin-bottom: 10px;
`;
const OverallGradeTitleWrapper = styled.div`
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
`;
const WrapperSec1 = styled.div`
  filter: drop-shadow(0px 0px 2.54px rgba(0, 0, 0, 0.26));
  background: #fff;
  border-radius: 7px;
`;
const DropdownSec = styled.div`
  background-color: ${COLOR.green100};
  min-height: 50px;
  width: 100%;
`;
const Dropdown = styled.button`
  width: 100%;
  height: 48px;
  background: #f4f5fa;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  border-width: 0px;
  cursor: pointer;
  justify-content: space-between;
  padding: 8px 15px;
  align-items: center;
`;
const DropdownTitle = styled.div`
  color: #000;
  font-family: Arial;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;
const DropdownIcon = styled.div`
  margin-top: -2px;
`;
const DropdownContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
`;
const DropdownContent = styled.div`
  display: ${(p) => (p.show ? "block" : "none")};
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 14px;
  top: 55px;
  border-radius: 7px;
  background: #fff;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.25);
  z-index: 999999;
  position: absolute;
  transition: display 2s;
  flex-direction: column;
  @media screen and (max-width: 680px) {
    right: 0px;
  }
`;
const DropdownItem = styled.button`
  //background-color: ${COLOR.blueish100};
  //color: ${COLOR.white};
  color: #000;
  cursor: pointer;
  text-align: center;
  font-family: Arial;
  font-size: 17px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  min-height: 40px;
  width: 100%;
  border: 1px solid transparent;
  border-radius: 8px;
  background: linear-gradient(#f8f9fb, #f8f9fb) padding-box,
    linear-gradient(90deg, #4d5ffe 0%, #9a57ed 100%) border-box;
`;
const SectionContainer = styled.div`
  // height:525px;
  width: 100%;
  overflow-y: auto;
`;
const SectionContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
const NoAssignmentImg = styled.img`
  height: 312px;
  width: initial;
  object-fit: initial;
`;
const DroDownMenu = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;
const Tab = styled.button`
  border: none;
  background-image: ${(p) => getBgColor(p.active)};
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  outline: none;
  padding: 8px 0px;
  width: 204px;
  min-height: 50px;
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: Arial;
  text-transform: capitalize;
  border-radius: 16px 16px 0px 0px;
  @media (max-width: 767px) {
    font-size: 13px;
    width: 133px;
  }
  @media (max-width: 467px) {
    font-size: 12px;
    width: 120px;
  }
  @media (max-width: 424px) {
    font-size: 10px;
    width: 108px;
  }
`;
// MUI v5 styled components
const StyledMenu = createStyledMuiComponent(Menu, {
  '& .MuiPaper-root': {
    border: '0px solid #d3d4d5',
    width: 200,
    marginTop: 2,
    backgroundColor: COLOR.white,
  },
});

const StyledMenuItem = createStyledMuiComponent(MenuItem, {
  backgroundColor: COLOR.blueish100,
  color: COLOR.white,
  textAlign: 'center',
  borderRadius: '10px',
  height: '30px',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '10px',
  borderWidth: '0px',
  paddingLeft: '10px',
  paddingRight: '10px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: COLOR.blueish200,
  },
  '&:focus': {
    backgroundColor: COLOR.blueish200,
    color: COLOR.white,
  },
  '&.Mui-selected': {
    backgroundColor: COLOR.blueish200,
  },
});

const DROPDOWNSORT = [
  { value: 'all', label: 'All' },
  { value: 'oldest_to_newest', label: 'Oldest to newest' },
  { value: 'newest_to_oldest', label: 'Newest to oldest' },
  { value: 'alphabetical_order', label: 'Alphabetical order' },
];

//  ----------------------------------------------------------------------------
//  Component
//  ----------------------------------------------------------------------------
//
//  Admin class visuals
//
//  -- Description
//  Admin view class visuals
//
//  -- Props
//  ...
//
//  -- State
//  ...
//
//  -- Methods
//  ...
//
//  -- Usage
//
const getKey = (sem, year) => `${sem == 1 ? 'semester 1' : 'semester 2'} - ${year}`;

const getCardData = (user_id, semData, submissions) => {
  const all_class_assignment = semData?semData:[]
  const examin_assignment = all_class_assignment.filter(k => k?.data?.is_exam);
  const unit_examin_assignment = all_class_assignment.filter(k => k?.data?.is_unit_exam);
  const activity_assignment = all_class_assignment.filter(k => !k?.data?.is_unit_exam && !k?.data?.is_exam);

  let examinAssigned = examin_assignment?.length;
  let unitExaminAssigned = unit_examin_assignment?.length;
  let activityAssigned = activity_assignment?.length;
  let activityCompleted = 0;
  let pastDueActivity = 0;
  let examinCompleted = 0;
  let pastDueExamin = 0;
  let unitExaminCompleted = 0;
  let pastDueUnitExamin = 0;
  activity_assignment.forEach(k => {
    const completed = filter(submissions, x => (x?.assignmentId == k?.activityId || x.assignmentId == k?.quizId) && x?.complete && user_id == x?.user?.id).length;
    
    activityCompleted += completed;
    if (!completed&&isBeforTime(get(k, "dueDate")) && (!k.lateDate || isBeforTime(get(k, "lateDate")))) {
      pastDueActivity += 1;
    }
  })
  examin_assignment.forEach(k => {
    const completed = filter(submissions, x => (x?.assignmentId == k?.activityId || x.assignmentId == k?.quizId) && x?.complete && user_id == x?.user?.id).length;
    examinCompleted += completed;
    if (!completed&&isBeforTime(get(k, "dueDate")) && (!k.lateDate || isBeforTime(get(k, "lateDate")))) {
      pastDueExamin += 1;
    }
  })
  unit_examin_assignment.forEach(k => {
    const completed = filter(submissions, x => (x?.assignmentId == k?.activityId || x.assignmentId == k?.quizId) && x?.complete && user_id == x?.user?.id).length;
    unitExaminCompleted += completed;
    if (!completed&&isBeforTime(get(k, "dueDate")) && (!k.lateDate || isBeforTime(get(k, "lateDate")))) {
      pastDueUnitExamin += 1;
    }
  })
  return {
    activity: {
      activityAssigned: activityAssigned,
      activityCompleted: activityCompleted,
      pastDueActivity: pastDueActivity,
    },
    examin: {
      examinAssigned: examinAssigned,
      examinCompleted: examinCompleted,
      pastDueExamin: pastDueExamin,
    },
    unitExamin: {
      unitExaminAssigned: unitExaminAssigned,
      unitExaminCompleted: unitExaminCompleted,
      pastDueUnitExamin: pastDueUnitExamin,
    }
  }
}

//to be remove end
const CardItem = ({
  title,
  value,
  isAplang,
  subValue,
  max,
  onClick = () => null,
  selected = false
}) => {
  const [size, setSize] = useState(116);
  const [fontSize, setFontSize] = useState(30); // default font size
  
  useEffect(() => {
    const updateDimensions = () => {
      const isMobile = window.innerWidth <= 480;
      setSize(isMobile ? 110 : 116);
      setFontSize(isMobile ? 24 : 30); // smaller font on mobile
    };

    updateDimensions(); // on mount
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const pct = max && max > 0 ? Math.round((value / max) * 100) : 0;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <>
      <div className={`stduntPrgrsInfo ${selected ? "selected" : ""}`} onClick={onClick}>
        <label>{title}</label>
        {!isAplang && <p>{value}</p>}
        {!isAplang && (
          <div
            style={{
              marginTop: 8,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <svg width={size} height={size}>
              <defs>
                <linearGradient
                  id="progressGradient"
                  x1="225"
                  y1="114"
                  x2="0"
                  y2="114"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#4D5FFE" />
                  <stop offset="1" stop-color="#F75CFA" />
                </linearGradient>
              </defs>
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="#f3f3fa"
                strokeWidth={strokeWidth}
                fill="none"
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="url(#progressGradient)"
                strokeWidth={strokeWidth}
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.5s" }}
              />
              <text
                x="50%"
                y="54%"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={fontSize}
                fontWeight="bold"
                fill="#111"
              >
                {pct}%
              </text>
            </svg>
          </div>
        )}
        <div className="stduntPrgrsLanUpr">
          {isAplang && <div className="stduntPrgrsLan">{value}</div>}
          {isAplang && <div className="stduntPrgrsLan">{subValue}</div>}
        </div>
      </div>
    </>
  );
};


function filterUniqueBySessionId(assignments, class_game_activity) {
  const seenSessionIds = new Set();

  return [...assignments, ...class_game_activity].filter((item) => {
    if (item.session_id) {
      if (seenSessionIds.has(item.session_id)) {
        return false;
      }
      seenSessionIds.add(item.session_id);
      return true;
    }
    return true;
  });
}

const ParentVisual = ({
  notes,
  questions,
  currentUser,
  submissions,
  allGraded,
  classActivities,
  classQuizzes,
  booksAllList,
  assignments,
  selectedUser,
}, ref) => {  
  const { activeStudentParent } = useActiveStudentofParent();
  let activeCurrentUserId = activeStudentParent;
  if (selectedUser && selectedUser.id) {
    activeCurrentUserId = selectedUser.id;
  }
  const { class_game_activity, fetchClassGameActivity } = useClassGameActivity();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const parentOrgId = useParentOrgId();
  const currentSem = getCurrenSem(parentOrgId);
  const resources = useResources();
  const servertime = useServertime();
  const timezone = useOrgTimezone();
  const exams = useExams();
  const apexams = useApExams();
  const story = useStory();
  const allresourcesVideo = flatMap(map(resources, (k) => k.videos));
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('all');
  const [bookSelection, setBookSelection] = useState('all');
  const [pastDueAssignmentArr, setPastDueAssignment] = useState([]);
  const [completedAssignmentArr, setCompletedAssignment] = useState([]);
  const [incompletedAssignmentArr, setInCompletedAssignment] = useState([]);
  const [todayPastDueAssignmentArr, setTodayPastDueAssignment] = useState([]);
  const [weekPastDueAssignmentArr, setWeekPastDueAssignment] = useState([]);
  const [allAssignment, setAllAssignment] = useState([]);
  const [showResponce, setShowResponce] = useState(-1);
  const [isdropdown, setIsDropdown] = useState(false);
  const activeFilterRef = useRef(null);
  const [activeSem, setActiveSem] = useState(currentSem);
  const [semAssignment, setSemAssignment] = useState({});
  const [activeSemData, setActiveSemData] = useState([]);
  const [semWiseNotes, setSemWiseNotes] = useState({});
  const [activeSemNotesLength, setActiveSemNotesLength] = useState(0);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [filterRef, isFilterVisible, setIsFilterVisible] = useComponentVisibleHook(false);
  const [bookfilterRef, isBookFilterVisible, setIsBookFilterVisible] =
    useComponentVisibleHook(false);
  const currentClass = useCurrentClass();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [bookAnchorEl, setBookAnchorEl] = useState(null);
  const userAllowedBooks = useUserAllowedBooks();
  const allowed_books = useCurrentClassBook();
  const { allowedbooks } = useCurrentClassActiveBooks();

  const [assignmentInfo, setAssignmentInfo] = useState({
    activity:{
      activityAssigned: 0,
      activityCompleted: 0,
      pastDueActivity: 0,
    },
    examin:{
      examinAssigned: 0,  
      examinCompleted: 0,
      pastDueExamin: 0,
    },
    unitExamin:{
      unitExaminAssigned: 0,
      unitExaminCompleted: 0,
      pastDueUnitExamin: 0,
    }
  });

  const [startTodayAssignment, setStartTodayAssignment] = useState([]);
  const [retakesAssignment, setRetakesAssignment] = useState([]);
  const [missingThisWeekAssignment, setMissingThisWeekAssignment] = useState([]);
  const [cardSelected, setCardSelected] = useState('');
  const [openGradebook, setOpenGradebook] = useState(false);
  const [sortfilterRef, isSortFilterVisible, setIsSortFilterVisible] =
    useComponentVisibleHook(false);
  const [sectionSort, setSectionSort] = useState('all');

  const assignment_data = useMemo(
    () => filterUniqueBySessionId(assignments, class_game_activity),
    [class_game_activity, assignments]
  );

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      let gradebookUserId = localStorage.getItem('gradebookUserId');
      if (
        (gradebookUserId && gradebookUserId === activeCurrentUserId) ||
        (!activeCurrentUserId && gradebookUserId === 'nocurrentuser')
      ) {
        setLoading(false);
      }
      if (activeCurrentUserId) {
        await Promise.allSettled([
          // dispatch(handleListUserNotes(activeCurrentUserId)),
          dispatch(handleListUserSubmissionsByClass(activeCurrentUserId, currentClass?.id)),
        ]);
        localStorage.setItem('gradebookUserId', activeCurrentUserId ?? 'nocurrentuser');
      }
      setLoading(false);
    }
    fetchData();
  }, [currentUser, dispatch]);

  // Expose to parent
  useImperativeHandle(ref, () => ({
    applyAllFiltersAndSort,
  }));
 

  useEffect(() => {
    const cardData = getCardData(activeStudentParent, semAssignment[activeSem], submissions)
    if (cardData) {
      setAssignmentInfo(cardData)
    }
  }, [activeStudentParent, submissions, semAssignment, activeSem])

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      // let gradebookUserId = localStorage.getItem("gradebookUserId");
      // if (
      //   (gradebookUserId && gradebookUserId === activeCurrentUserId) ||
      //   (!activeCurrentUserId && gradebookUserId === "nocurrentuser")
      // ) {
      //   setLoading(false);
      // }
      if (activeCurrentUserId) {
        await Promise.allSettled([
          dispatch(handleListUserNotes(activeCurrentUserId)),
          // dispatch(handleListUserSubmissions(activeCurrentUserId)),
          dispatch(
            handleListUserSubmissionsByClass(
              activeCurrentUserId,
              currentClass?.id
            )
          ),
          fetchClassGameActivity(),
        ]);
        const getAssignmentCount = await dispatch(
          handleGetStudentAssignmentCount(currentClass?.id)
        );
        // setTotalAssignmentCount(getAssignmentCount);
        localStorage.setItem(
          "gradebookUserId",
          activeCurrentUserId ?? "nocurrentuser"
        );
      }
      setLoading(false);
    }
    fetchData();
  }, [currentUser]);

  const getWeightedGrade = (grade, weight) => {
    if (weight === 0) {
      return 0;
    }
    return (grade * weight) / 100;
  };

  const getSortTitle = () => {
    const obj = find(DROPDOWNSORT, ['value', sectionSort]);
    if (obj) return obj.label;
    return 'All';
  };

  const getGrade = (submission) => {
    let isTbgObj = checkIsManualGradable(get(submission, 'assignment.questions'));
    return submission?.manualGrading && isTbgObj
      ? 'TBG'
      : submission?.grade
        ? submission?.grade
        : 0;
  };
  const setOverAllChartData = () => {
    let _assignments = 0;
    let total = 0;
    activeSemData.map((item) => {
      let grade = getGrade(item.submission);
      if (!isNaN(parseInt(grade))) {
        _assignments++;
        let weightage = parseInt(item.weightage) || 100;
        let _grd = getWeightedGrade(parseInt(grade, 10), weightage);
        total += parseInt(_grd, 10);
      }
    });
    let grade = (parseInt(total, 10) / parseInt(_assignments, 10)).toFixed(0);
    grade = isNaN(grade) ? 0 : grade;
    const remain = 100 - grade;
    config.data = [
      { name: "Overall Grade", value: parseInt(grade, 10), fill: chartColors[0] },
      { name: "", value: remain, fill: chartColors[1] },
    ];
    config.title = `${grade ?? ""}%`;
  };
  const sortByDueDate = (arr) => {
    return orderBy(arr, ['dueDate'], ['desc']);
  };
  useEffect(() => {
    filterAssignmentsBasedOnsem();
  }, [activeSemData, activeSem, semAssignment]);
  const getAssignmentDate = (assignment) => {
    if (assignment) {
      if (assignment.lateDate && assignment.lateDate.length > 0) {
        return assignment.lateDate;
      } else {
        return assignment.dueDate;
      }
    } else {
      return null;
    }
  };
  const filterCompletedAssignment = (assignment) => {
    if (!assignment) return false;
    return assignment.submission && assignment.submission.complete && !assignment.retaking;
  };

  const filterPastdueAssignment = (assignment) => {
    if (!assignment) return false;
    const isCompleted =
      has(assignment, 'submission.complete') && get(assignment, 'submission.complete', true);
    return !!(isBeforTime(assignment.dueDate, servertime, timezone) && !isCompleted);
  };
  const filterInCompleteAssignment = (assignment) => {
    if (!assignment) return false;
    // const isNotCompleted = checkNotCompleted(assignment)
    return (
      !isBeforTime(getAssignmentDate(assignment), servertime, timezone) &&
      (get(assignment, 'submission.submissions', 0) == 0 || get(assignment, 'submission.retaking'))
    );
  };
  const filterTodayAssignment = (assignment) => {
    if (!assignment) return false;
    const _lateDate = get(assignment, 'lateDate');
    const _dueDate = get(assignment, 'dueDate');
    const isCompleted =
      has(assignment, 'submission.complete') && get(assignment, 'submission.complete', true);
    return (
      (moment(_lateDate).isSame(moment(), 'days') || moment(_dueDate).isSame(moment(), 'days')) &&
      !isCompleted
    );
  };
  const filterWeekAssignment = (assignment) => {
    if (!assignment) return false;
    const _lateDate = get(assignment, 'lateDate');
    const _dueDate = get(assignment, 'dueDate');
    const isCompleted =
      has(assignment, 'submission.complete') && get(assignment, 'submission.complete', true);
    return (
      (moment(_lateDate).isSame(moment(), 'week') || moment(_dueDate).isSame(moment(), 'week')) &&
      !isCompleted
    );
  };

  const checkNotCompleted = (assignment) => {
    return (
      (get(assignment, "submission.submissions", 0) <=
        get(assignment, "retakes", 0) ||
        get(assignment, "submission.retaking")) &&
      !(
        get(assignment, "submission.submissions", 0) == 0 &&
        !get(assignment, "submission.retaking")
      )
    );
  };

  const filterWeekAssignmentPast = (assignment) => {
    if (!assignment) return false;
    const _dueDate = get(assignment, "lateDate");
    return moment(_dueDate).isSame(moment(), "week");
  };

  const filterInRetakeAssignment = (assignment) => {
    if (!assignment) return false;
    const isNotCompleted = checkNotCompleted(assignment);
    const has_time = !isBeforTime(
      getAssignmentDate(assignment),
      servertime,
      timezone
    );
    const has_submission = get(assignment, "submission.submissions", 0) > 0;
    const has_retaking = !get(assignment, "submission.retaking");
    return isNotCompleted && has_time && has_submission && has_retaking;
  };

  const filterStartWeekAssignment = (assignment) => {
    if (!assignment) return false;
    const _dueDate = get(assignment, "scheduleAt");
    return moment(_dueDate).isSame(moment(), "week");
  };

  const filterAssignmentsBasedOnsem = () => {
    const assignmentWithSubmission = semAssignment[activeSem];
    setAllAssignment(assignmentWithSubmission);
    // setFilteredAssignments(assignmentWithSubmission)
    const pastDueAssignment = sortByDueDate(
      filter(assignmentWithSubmission, filterPastdueAssignment)
    );
    const completedAssignment = sortByDueDate(
      filter(assignmentWithSubmission, filterCompletedAssignment)
    );
    const todayPastDueAssignment = sortByDueDate(
      filter(assignmentWithSubmission, filterTodayAssignment)
    );
    const weekPastDueAssignment = sortByDueDate(filter(assignmentWithSubmission, filterWeekAssignment));    
    const _pastDueQuizCount = pastDueAssignment.filter(
      (assignment) => assignment.data?.typeId === "quiz"
    ).length;
    const _pastDueActivityCount = pastDueAssignment.filter(
      (assignment) => assignment.data?.typeId === "activity"
    ).length;
    const _completeQuizCount = completedAssignment.filter(
      (assignment) => assignment.data?.typeId === "quiz"
    ).length;
    const _completeActivityCount = completedAssignment.filter(
      (assignment) => assignment.data?.typeId === "activity"
    ).length;
    const _incompletedAssignmentArr = sortByDueDate(
      filter(assignmentWithSubmission, filterInCompleteAssignment)
    );
    const _retakingAssignmentArr = sortByDueDate(
      filter(assignmentWithSubmission, filterInRetakeAssignment)
    );

    const weekStartAssignment = filter(
      assignmentWithSubmission,
      filterStartWeekAssignment
    );
    const _startToday = filterStudentSubmission(
      weekStartAssignment,
      "start",
      assignmentWithSubmission
    );
    // const _missingThisWeek = filterStudentSubmission(weekStartAssignment, 'missing',assignmentWithSubmission);
    const weekAssignmentPast = filter(
      assignmentWithSubmission,
      filterWeekAssignmentPast
    );
    const _missingThisWeek = filterStudentSubmission(
      weekAssignmentPast,
      "missing",
      assignmentWithSubmission
    );

    setPastDueAssignment(pastDueAssignment);
    setCompletedAssignment(completedAssignment);
    setWeekPastDueAssignment(weekPastDueAssignment);
    setTodayPastDueAssignment(todayPastDueAssignment);
    // setPastDueActivityCount(_pastDueActivityCount);
    // setPastDueQuizCount(_pastDueQuizCount);
    // setCompletedActivityCount(_completeActivityCount);
    // setCompletedQuizCount(_completeQuizCount);
    setInCompletedAssignment(_incompletedAssignmentArr);
    setStartTodayAssignment(_startToday);
    setMissingThisWeekAssignment(_missingThisWeek);
    setRetakesAssignment(_retakingAssignmentArr);
    onGetSelectionChange(
      section,
      bookSelection,
      assignmentWithSubmission,
      todayPastDueAssignment,
      weekPastDueAssignment,
      pastDueAssignment,
      completedAssignment,
      _incompletedAssignmentArr,
      _retakingAssignmentArr
    );
  };

  const onGetSelectionChange = (
    value,
    bookVal,
    assignmentWithSubmission,
    todayPastDueAssignment,
    weekPastDueAssignment,
    pastDueAssignment,
    completedAssignment,
    _incompletedAssignmentArr,
    _retakingAssignmentArr
  ) => {
    activeFilterRef.current = "all";
    const obj = find(DROPDOWN, ["value", value]);
    if (obj) {
      switch (value) {
        case "all":
          setFilteredAssignments(
            filterdBasedOnBook(assignmentWithSubmission, bookVal)
          );
          break;
        case "due_today":
          setFilteredAssignments(
            filterdBasedOnBook(todayPastDueAssignment, bookVal)
          );
          break;
        case "due_this_week":
          setFilteredAssignments(
            filterdBasedOnBook(weekPastDueAssignment, bookVal)
          );
          break;
        case "due_past":
          setFilteredAssignments(
            filterdBasedOnBook(getPastData(pastDueAssignment), bookVal)
          );
          break;
        case "completed":
          setFilteredAssignments(
            filterdBasedOnBook(getCompletedData(completedAssignment), bookVal)
          );
          break;
        case "incompleted":
          setFilteredAssignments(
            filterdBasedOnBook(
              getInCompletedData(_incompletedAssignmentArr),
              bookVal
            )
          );
          break;
        case "retakes":
          setFilteredAssignments(
            filterdBasedOnBook(getRetakesData(_retakingAssignmentArr), bookVal)
          );
          break;
        default:
          setFilteredAssignments(
            filterdBasedOnBook(assignmentWithSubmission, bookVal)
          );
          break;
      }
    } else {
      setFilteredAssignments(
        filterdBasedOnBook(assignmentWithSubmission, bookVal)
      );
    }
  };

  const getPastData = (pastDueAssignment) => {
    if (activeFilterRef.current == "all") {
      return pastDueAssignment;
    } else if (activeFilterRef.current == "quiz") {
      return pastDueAssignment.filter(
        (assignment) => assignment.data?.typeId === "quiz"
      );
    } else if (activeFilterRef.current == "activity") {
      return pastDueAssignment.filter(
        (assignment) => assignment.data?.typeId === "activity"
      );
    } else {
      return pastDueAssignment;
    }
  };

  const getCompletedData = (completedAssignment) => {
    if (activeFilterRef.current == "all") {
      return completedAssignment;
    } else if (activeFilterRef.current == "quiz") {
      return completedAssignment.filter(
        (assignment) => assignment.data?.typeId === "quiz"
      );
    } else if (activeFilterRef.current == "activity") {
      return completedAssignment.filter(
        (assignment) => assignment.data?.typeId === "activity"
      );
    } else {
      return completedAssignment;
    }
  };

  useEffect(() => {
    const userAssignment = getUserAssignment(
      allowed_books,
      assignment_data,
      currentUser,
      currentClass,
      userAllowedBooks?.other_permissions?.resourses,
      activeStudentParent
    );
    const withSubmission = map(userAssignment, (assignment) => {
      const is_game = assignment.type === "game";
      const is_debate = assignment.type === "debate";
      const is_worksheet = assignment.type === "worksheet";
      const is_loteria = assignment.type === "bingo";
      const chapter_id =
        is_debate || is_game || is_loteria || is_worksheet
          ? get(assignment, "assignment.chapter")
          : get(assignment, "data.chapter");
      const assignmentId =
        is_debate || is_game || is_loteria || is_worksheet
          ? get(assignment, "session_id")
          : get(assignment, "data.id");
      let book = find(booksAllList, (k) => k.id == chapter_id);
      try {
        if (!book) {
          book = find(allresourcesVideo, { id: chapter_id });
          if (!book && isVideo(get(assignment, "data.chapter"), resources)) {
            book = find(allresourcesVideo, {
              id: chapter_id?.substring(0, 36),
            });
          }
        }
      } catch (error) {
        Sentry.captureException(`555 Error in getUserAssignment: ${error}`);
      }
      const submi = find(submissions, ["assignmentId", assignmentId]);

      if (
        submi &&
        (!submi.assignment || Object.keys(submi.assignment).length === 0)
      ) {
        submi.assignment = assignment.assignment;
      }
      return {
        ...assignment,
        submission: find(submissions, ["assignmentId", assignmentId]),
        data:
          is_game || is_debate || is_loteria || is_worksheet
            ? assignment.assignment
            : assignment.data,
        book: book ? book : {},
      };
    });
    let assignmentWithSubmission = filter(withSubmission, "book");
    const isSpecial = parentOrgId && ['e5dcb06f-f78d-457a-bacf-8fa357b38ee2', '37ae9554-0da4-4d62-b04f-ec500b0275a4'].includes(parentOrgId);
    
    let yearData = groupBy(assignmentWithSubmission, (x) =>
      moment(x.dueDate).format("YYYY")
    );
    const semData = {};
      Object.keys(yearData).map((year) => {
        const yearNum = parseInt(year);
        let sem1StartDate, sem1EndDate, sem2StartDate, sem2EndDate;
        let sem1, sem2;
        
        if (isSpecial) {
          const sem1Dates = semeterDateByYear(yearNum, parentOrgId);
          const sem2Dates = semeterDateByYear(yearNum, parentOrgId);
          sem1StartDate = sem1Dates.sem1StartDate;
          sem1EndDate = sem1Dates.sem1EndDate;
          sem2StartDate = sem2Dates.sem2StartDate;
          sem2EndDate = sem2Dates.sem2EndDate;
          
          const prevYearSem1Dates = semeterDateByYear(yearNum - 1, parentOrgId);
          const prevYearSem1StartDate = prevYearSem1Dates.sem1StartDate;
          const prevYearSem1EndDate = prevYearSem1Dates.sem1EndDate;
          
          sem1 = yearData[year].filter((x) => {
            const assignmentDate = moment(x.dueDate);
            const assignmentMonth = assignmentDate.month();
            const assignmentDay = assignmentDate.date();
            const sem1EndDay = getSem1EndDate(parentOrgId);
            
            if (assignmentMonth === 0 && assignmentDay <= sem1EndDay) {
              return assignmentDate.isSameOrAfter(prevYearSem1StartDate, "day") && assignmentDate.isSameOrBefore(prevYearSem1EndDate, "day");
            } else {
              return assignmentDate.isSameOrAfter(sem1StartDate, "day") && assignmentDate.isSameOrBefore(sem1EndDate, "day");
            }
          });
          
          sem2 = yearData[year].filter((x) => {
            const assignmentDate = moment(x.dueDate);
            return assignmentDate.isSameOrAfter(sem2StartDate, "day") && assignmentDate.isSameOrBefore(sem2EndDate, "day");
          });
        } else {
          const dates = semeterDateByYear(yearNum, parentOrgId);
          sem1StartDate = dates.sem1StartDate;
          sem1EndDate = dates.sem1EndDate;
          sem2StartDate = dates.sem2StartDate;
          sem2EndDate = dates.sem2EndDate;
          
          sem1 = yearData[year].filter(
            (x) =>
              moment(x.dueDate).isSameOrAfter(sem1StartDate, "day") &&
              moment(x.dueDate).isSameOrBefore(sem1EndDate, "day")
          );
          sem2 = yearData[year].filter(
            (x) =>
              moment(x.dueDate).isSameOrAfter(sem2StartDate, "day") &&
              moment(x.dueDate).isSameOrBefore(sem2EndDate, "day")
          );
        }

        if (sem1.length > 0 || currentSem === getKey(1, year)) {
          semData[getKey(1, year)] = sem1;
        }
        if (sem2.length > 0 || currentSem === getKey(2, year)) {
          semData[getKey(2, year)] = sem2;
        }
      });
      if (semData[currentSem]) {
        setActiveSemData(semData[currentSem]);
      }
      if (Object.keys(semData).length > 0) {
        let sem_data = {};
        const current_year = moment().format("YYYY");
        const prevYear = parseInt(current_year) - 1;

        const semesters = {
          currentYearSem1: getKey(1, current_year),
          currentYearSem2: getKey(2, current_year),
          prevYearSem1: getKey(1, prevYear),
          prevYearSem2: getKey(2, prevYear),
        };

        const addSemData = (key) => {
          if (semData[key]) {
            sem_data[key] = semData[key];
            return true;
          }
          return false;
        };

        sem_data[currentSem] = semData[currentSem];

        if (currentSem === semesters.currentYearSem1) {
          addSemData(semesters.currentYearSem2) ||
            addSemData(semesters.prevYearSem1) ||
            addSemData(semesters.prevYearSem2);
        } else if (currentSem === semesters.currentYearSem2) {
          addSemData(semesters.currentYearSem1) ||
            addSemData(semesters.prevYearSem1) ||
            addSemData(semesters.prevYearSem2);
        }

        setSemAssignment(sem_data);
      }
    
  }, [assignment_data, submissions, resources , allowed_books, activeStudentParent, parentOrgId, currentSem]);
  useEffect(setOverAllChartData, [activeSemData, activeSem]);

  useEffect(() => {
    let yearData = groupBy(notes, (x) => moment(parseInt(x.createdAt)).format('YYYY'));
    const semNotes = {};
    const isSpecial = parentOrgId && ['e5dcb06f-f78d-457a-bacf-8fa357b38ee2', '37ae9554-0da4-4d62-b04f-ec500b0275a4'].includes(parentOrgId);
    
    Object.keys(yearData).map((year) => {
      const yearNum = parseInt(year);
      let sem1StartDate, sem1EndDate, sem2StartDate, sem2EndDate;
      let sem1, sem2;
      
      if (isSpecial) {
        const sem1Dates = semeterDateByYear(yearNum, parentOrgId);
        const sem2Dates = semeterDateByYear(yearNum, parentOrgId);
        sem1StartDate = sem1Dates.sem1StartDate;
        sem1EndDate = sem1Dates.sem1EndDate;
        sem2StartDate = sem2Dates.sem2StartDate;
        sem2EndDate = sem2Dates.sem2EndDate;
        
        const prevYearSem1Dates = semeterDateByYear(yearNum - 1, parentOrgId);
        const prevYearSem1StartDate = prevYearSem1Dates.sem1StartDate;
        const prevYearSem1EndDate = prevYearSem1Dates.sem1EndDate;
        
        sem1 = yearData[year].filter((x) => {
          const noteDate = moment(parseInt(x.createdAt));
          const noteMonth = noteDate.month();
          const noteDay = noteDate.date();
          const sem1EndDay = getSem1EndDate(parentOrgId);
          
          if (noteMonth === 0 && noteDay <= sem1EndDay) {
            return noteDate.isSameOrAfter(prevYearSem1StartDate, 'day') && noteDate.isSameOrBefore(prevYearSem1EndDate, 'day');
          } else {
            return noteDate.isSameOrAfter(sem1StartDate, 'day') && noteDate.isSameOrBefore(sem1EndDate, 'day');
          }
        });
        
        sem2 = yearData[year].filter((x) => {
          const noteDate = moment(parseInt(x.createdAt));
          return noteDate.isSameOrAfter(sem2StartDate, 'day') && noteDate.isSameOrBefore(sem2EndDate, 'day');
        });
      } else {
        const dates = semeterDateByYear(yearNum, parentOrgId);
        sem1StartDate = dates.sem1StartDate;
        sem1EndDate = dates.sem1EndDate;
        sem2StartDate = dates.sem2StartDate;
        sem2EndDate = dates.sem2EndDate;
        
        sem1 = yearData[year].filter(
          (x) =>
            moment(parseInt(x.createdAt)).isSameOrAfter(sem1StartDate, 'day') &&
            moment(parseInt(x.createdAt)).isSameOrBefore(sem1EndDate, 'day'),
        );
        sem2 = yearData[year].filter(
          (x) =>
            moment(parseInt(x.createdAt)).isSameOrAfter(sem2StartDate, 'day') &&
            moment(parseInt(x.createdAt)).isSameOrBefore(sem2EndDate, 'day'),
        );
      }

      if (sem1.length > 0 || currentSem === getKey(1, year)) {
        semNotes[getKey(1, year)] = sem1;
      }
      if (sem2.length > 0 || currentSem === getKey(2, year)) {
        semNotes[getKey(2, year)] = sem2;
      }
    });
    if (!semNotes[currentSem]) {
      semNotes[currentSem] = [];
    }
    setActiveSemNotesLength(semNotes[currentSem]?.length ?? 0);
    setSemWiseNotes(semNotes);
  }, [notes, parentOrgId, currentSem]);

  const todayDate = moment();
  const apExamLAN = dayjs("14/05/2026", "DD/MM/YYYY");
  const apExamLIT = dayjs("13/05/2026", "DD/MM/YYYY");

  const getTitle = () => {
    const obj = find(DROPDOWN, ['value', section]);
    if (obj) return obj.label;
    return 'All';
  };

  const bookForFilter = useMemo(() => {
    let allBook = [
      { bookId: "all", description: "All" },
      { bookId: "debate", description: "Debate" },
      { bookId: "worksheet", description: "Grammar drill" },
      { bookId: "bingo", description: "Lotería" },
      { bookId: "story", description: "Story" },
      { bookId: "ap", description: "AP Exams" },
      { bookId: "exams", description: "Exams" },
      { bookId: "game", description: "1Game" },
    ];
    const filterBooks = [...allBook, ...allowedbooks.map(book => ({ bookId: book.id, description: book.description }))];
    return uniqBy(filterBooks, "bookId");
  }, [allowedbooks]);

  const getTitleBookFilterTitle = () => {
    const bookData = find(bookForFilter, ['bookId', bookSelection]);
    if (bookData) {
      return get(bookData, 'description', '');
    }
    return 'All';
  };

  const filterdBasedOnBook = (arr, bookVal) => {
    if (bookVal == "all" || !bookVal) {
      return arr;
    } else if (bookVal === "ap") {
      return filter(arr, (k) =>
        apexams
          ?.flatMap((k) => k.exams)
          .map((n) => `${n.bookId}`)
          .includes(get(k, "data.bookId"))
      );
    } else if (bookVal === "story") {
      return filter(arr, (k) =>
        story?.map((n) => `${n.bookId}`).includes(get(k, "data.bookId"))
      );
    } else if (bookVal === "debate") {
      return filter(arr, (k) => k?.type === "debate");
    } else if (bookVal === "worksheet") {
      return filter(arr, (k) => k?.type === "worksheet");
    } else if (bookVal === "bingo") {
      return filter(arr, (k) => k?.type === "bingo");
    } else if (bookVal === "exams") {
      return filter(arr, (k) =>
        exams
          ?.flatMap((k) => k.exams)
          .map((n) => `${n.bookId}`)
          .includes(get(k, "data.bookId"))
      );
    } else if (bookVal === "game") {
      return filter(arr, (k) => k?.type === "game" || k?.type === "game");
    } else {
      const arrval = filter(
        arr,
        (k) =>
          get(k, "data.bookId") == bookVal || get(k, "data.chapter") == bookVal 
      );
      return arrval;
    }
  };

  const onselectionchange = (value, bookVal, type) => {
    setShowResponce(-1);
    if (type === "book") {
      setIsBookFilterVisible(false);
      setBookSelection(bookVal);
    } else {
      setIsFilterVisible(false);
      setSection(value);
    }
    setIsDropdown(false);
    // activeFilterRef.current = "all";
    applyAllFiltersAndSort(value, bookVal, sectionSort); // <-- unified function
  };

  const getRetakesData = (_retakingAssignmentArr) => {
    if (activeFilterRef.current == "all") {
      return _retakingAssignmentArr;
    } else if (activeFilterRef.current == "quiz") {
      return _retakingAssignmentArr.filter(
        (assignment) => assignment.data?.typeId === "quiz"
      );
    } else if (activeFilterRef.current == "activity") {
      return _retakingAssignmentArr.filter(
        (assignment) => assignment.data?.typeId === "activity"
      );
    } else {
      return _retakingAssignmentArr;
    }
  };

  const onSortSelectionChange = (value, bookVal, type) => {
    setIsSortFilterVisible(false);
    setSectionSort(value);
    setIsDropdown(false);
    applyAllFiltersAndSort(section, bookVal, value); // <-- unified function
  };

  const applyAllFiltersAndSort = (filterSection = section, bookVal, sortType = sectionSort, searchText = "") => {
    let baseData = [];
    // 1. Apply filters first
    const sectionObj = find(DROPDOWN, ["value", filterSection]);
    if (sectionObj || bookVal || filterSection === "all") {
      switch (filterSection) {
        case "all":
          baseData = filterdBasedOnBook(allAssignment, bookVal);
          break;
        case "due_today":
          baseData = filterdBasedOnBook(filterActiveTasks(todayPastDueAssignmentArr), bookVal);
          break;
        case "due_this_week":
          baseData = filterdBasedOnBook(weekPastDueAssignmentArr, bookVal);
          break;
        case "due_past":
          baseData = filterdBasedOnBook(getPastDueAssignment(), bookVal);
          break;
        case "completed":
          baseData = filterdBasedOnBook(getCompletedAssignment(), bookVal);
          break;
        case "incompleted":
          baseData = filterdBasedOnBook(getInCompletedAssignment(), bookVal);
          break;
        case "retakes":
          baseData = filterdBasedOnBook(getRetakesData(retakesAssignment), bookVal);
          break;
        case "startToday":
          baseData = filterdBasedOnBook(startTodayAssignment, bookVal);
          break;
        case "missing_this_week":
          baseData = filterdBasedOnBook(missingThisWeekAssignment, bookVal);
          break;
        default:
          baseData = filterdBasedOnBook(allAssignment, bookVal);
          break;
      }
    } else {
      baseData = [...allAssignment]; // Fallback
    }
  
    // 2. Apply search on the filtered data
    let filtered = baseData;
    if (searchText.trim()) {
      filtered = baseData.filter(item =>
        (item.displayName || "").toLowerCase().includes(searchText.toLowerCase())
      );
    }
  
    // 3. Apply sort on the searched + filtered data
    switch (sortType) {
      case "oldest_to_newest":
        filtered = filtered
          .slice()
          .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        break;
      case "newest_to_oldest":
        filtered = filtered
          .slice()
          .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));
        break;
      case "alphabetical_order":
        filtered = filtered.slice().sort((a, b) => {
          const nameA = (a.displayName || "").toLowerCase();
          const nameB = (b.displayName || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      default:
        // No sort
        break;
    }
    setFilteredAssignments(filtered);
  };

  const getNoAssignmentBg = (value) => {
    switch (value) {
      case "all":
        return nothingToSee;
      case "due_today":
        return nothingToSee;
      case "due_this_week":
        return nothingToSee;
      case "due_past":
        return nothingToSee;
      case "completed":
        return nothingToSee;
      case "incompleted":
        return nothingToSee;
      default:
        return nothingToSee;
    }
  };

  const fetchAssignmentSubmission = (assignment) => {
    if (
      assignment?.type === "debate" ||
      assignment.type === "bingo" ||
      assignment.type == "worksheet" ||
      assignment.type === "game"
    ) {
      const assignmentSubmissions = get(assignment, "submission");
      const game = find(class_game_activity, [
        "session_id",
        assignmentSubmissions?.assignmentId,
      ]);
      if (game) {
        const submissionData = {
          ...assignmentSubmissions,
          assignment: game.assignment,
        };
        return submissionData;
      } else {
        return assignmentSubmissions;
      }
    } else {
      return get(assignment, "submission");
    }
  };

  const getPastDueAssignment = () => {
    if (activeFilterRef.current == "all") {
      return pastDueAssignmentArr;
    } else if (activeFilterRef.current == "quiz") {
      return pastDueAssignmentArr.filter(
        (assignment) => (assignment.data?.typeId === "quiz" && !assignment.data?.is_exam && !assignment.data?.is_unit_exam)
      );
    } else if (activeFilterRef.current == "examen_past") {
      return pastDueAssignmentArr.filter((assignment) => assignment.data?.is_exam);
    } else if (activeFilterRef.current == "unit_past") {
      return pastDueAssignmentArr.filter((assignment) => assignment.data?.is_unit_exam);
    } else {
      return pastDueAssignmentArr;
    }
  };

  const getCompletedAssignment = () => {
    if (activeFilterRef.current == "all") {
      return completedAssignmentArr;
    } else if (activeFilterRef.current == "quiz") {
      return completedAssignmentArr.filter(
        (assignment) => (assignment.data?.typeId === "quiz" && !assignment.data?.is_exam && !assignment.data?.is_unit_exam)
      );
    } else if (activeFilterRef.current == "activity") {
      return completedAssignmentArr.filter(
        (assignment) => (assignment.data?.typeId === "activity" && !assignment.data?.is_exam && !assignment.data?.is_unit_exam)
      );
    } else if (activeFilterRef.current == 'activity_completed') {
      return completedAssignmentArr.filter(
        (assignment) => (assignment.data?.typeId === "activity")
      );
    } else if (activeFilterRef.current == 'Examen_complete') {
      return completedAssignmentArr.filter(
        (assignment) => assignment.data?.is_exam
      );
    } else if (activeFilterRef.current == 'unit_completed') {
      return completedAssignmentArr.filter(
        (assignment) => assignment.data?.is_unit_exam
      );
    } else {
      return completedAssignmentArr;
    }
  };

  const getInCompletedAssignment = () => {
    if (activeFilterRef.current == "all") {
      return incompletedAssignmentArr;
    } else if (activeFilterRef.current == "quiz") {
      return incompletedAssignmentArr.filter(
        (assignment) => assignment.data?.typeId === "quiz"
      );
    } else if (activeFilterRef.current == "activity") {
      return incompletedAssignmentArr.filter(
        (assignment) => assignment.data?.typeId === "activity"
      );
    } else {
      return incompletedAssignmentArr;
    }
  };

  const getInCompletedData = (_incompletedAssignmentArr) => {
    if (activeFilterRef.current == "all") {
      return _incompletedAssignmentArr;
    } else if (activeFilterRef.current == "quiz") {
      return _incompletedAssignmentArr.filter(
        (assignment) => assignment.data?.typeId === "quiz"
      );
    } else if (activeFilterRef.current == "activity") {
      return _incompletedAssignmentArr.filter(
        (assignment) => assignment.data?.typeId === "activity"
      );
    } else {
      return _incompletedAssignmentArr;
    }
  };

  const renderGrades = useMemo(() => {
  const getHiddenBooks =
    get(currentClass, "hide_books", []).map((data) => data?.bookId) || [];
  const filterAssignmentNew = filteredAssignments
    ?.map((assignment) => {
      if (
      //   get(currentUser, "role") == "student" &&
        getHiddenBooks?.includes(assignment?.book?.id)
      ) {
        return;
      }
      return assignment;
    })
    ?.filter((x) => x !== undefined);
  if (
    filterAssignmentNew?.length === 0 ||
    filterAssignmentNew === undefined
  ) {
    return (
      <div className="noAssignImg">
        <NoAssignmentImg src={getNoAssignmentBg(section)} />
      </div>
    );
  } else {
    return map(filterAssignmentNew, (assignment, i) => {
      return (

        <AssignmentStatusParent
          key={assignment.id}
          chapter={get(assignment, "data.chapter")}
          data={assignment}
          submission={fetchAssignmentSubmission(assignment)}
          showResponce={showResponce == i}
          onShowResponce={() => setShowResponce(showResponce == i ? -1 : i)}
          book={get(assignment, "book")}
          booksAllList={booksAllList}
          currentClass={currentClass}
          assignment={assignment}
        />
      );
    });
  }
  }, [filteredAssignments, showResponce, booksAllList]);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
    setIsFilterVisible(true);
  };

  const handleBookFilterClick = (event) => {
    setBookAnchorEl(event.currentTarget);
    setIsBookFilterVisible(true);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setIsFilterVisible(false);
  };

  const handleBookFilterClose = () => {
    setBookAnchorEl(null);
    setIsBookFilterVisible(false);
  };

  return (
    <SentryWrap>
      <Wrapper>
        {loading ? (
        <Loading />
      ) : (
        <>
          <Row className="studentVisualsRow">
            <Col
              xs={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 17 }}
              style={{ marginBottom: 20 }}
            >
              <div className="ovrAllGrade">
                <div className="ovrAllGradeCmn ovrAllGradeLeft">
                  <div style={{ position: 'relative', width: chartConfig.width, height: chartConfig.height }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                      <defs>
                          <linearGradient
                            id="pieGradient"
                            x1="225"
                            y1="114"
                            x2="0"
                            y2="114"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stop-color="#4D5FFE" />
                            <stop offset="1" stop-color="#F75CFA" />
                          </linearGradient>
                        </defs>
                        <Pie
                          data={config.data}
                          cx="50%"
                          cy="50%"
                          outerRadius={chartConfig.outerRadius}
                          innerRadius={chartConfig.innerRadius}
                          paddingAngle={0}
                          dataKey="value"
                          nameKey="name"
                          label={false}
                        >
                          {config.data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      color: 'black',
                      ...chartConfig.titleStyle
                    }}>
                      {config.title}
                    </div>
                  </div>
                  <div>
                    <p>This is your overall grade for this class</p>
                  </div>
                </div>

                <div className="ovrAllGradeCmn ovrAllGradeRght">
                  <div className="ovrAllGradeRghtIner">
                    <SearchByTitle>Search by</SearchByTitle>

                    <div className="ovrAllGradeDrop">
                      <DropdownContainer ref={filterRef}>
                        <Dropdown
                          onClick={() => {
                            setIsFilterVisible(!isFilterVisible);
                            setIsDropdown(!isdropdown);
                          }}
                        >
                          <DropdownTitle>{getTitle()}</DropdownTitle>
                          <DropdownIcon>
                            {isdropdown && isFilterVisible ? (
                              <KeyboardArrowUp />
                            ) : (
                              <KeyboardArrowDown />
                            )}
                          </DropdownIcon>
                        </Dropdown>
                        <DropdownContent show={isFilterVisible}>
                          {DROPDOWN.map((x) => (
                            <DropdownItem
                              key={x.value}
                              onClick={() =>
                                onselectionchange(x.value, bookSelection)
                              }
                            >
                              {x.label}
                            </DropdownItem>
                          ))}
                        </DropdownContent>
                      </DropdownContainer>

                      <DropdownContainer ref={bookfilterRef}>
                        <Dropdown
                          onClick={() => {
                            setIsBookFilterVisible(!isBookFilterVisible);
                            setIsDropdown(!isdropdown);
                          }}
                        >
                          <DropdownTitle>
                            {getTitleBookFilterTitle()}
                          </DropdownTitle>
                          <DropdownIcon>
                            {isdropdown && isBookFilterVisible ? (
                              <KeyboardArrowUp />
                            ) : (
                              <KeyboardArrowDown />
                            )}
                          </DropdownIcon>
                        </Dropdown>
                        <DropdownContent show={isBookFilterVisible}>
                          {map(bookForFilter, (x) => (
                            <DropdownItem
                              key={x?.bookId}
                              onClick={() =>
                                onselectionchange(section, x?.bookId, "book")
                              }
                            >
                              {get(x, "description")}
                            </DropdownItem>
                          ))}
                        </DropdownContent>
                      </DropdownContainer>

                      <DropdownContainer ref={sortfilterRef}>
                        <Dropdown
                          onClick={() => {
                            setIsSortFilterVisible(!isSortFilterVisible);
                            setIsDropdown(!isdropdown);
                          }}
                        >
                          <DropdownTitle>{getSortTitle()}</DropdownTitle>
                          <DropdownIcon>
                            {isdropdown && isSortFilterVisible ? (
                              <KeyboardArrowUp />
                            ) : (
                              <KeyboardArrowDown />
                            )}
                          </DropdownIcon>
                        </Dropdown>
                        <DropdownContent show={isSortFilterVisible}>
                          {map(DROPDOWNSORT, (x) => (
                            <DropdownItem
                              key={x.bookId}
                              onClick={() =>
                                onSortSelectionChange(x.value, bookSelection)
                              }
                            >
                              {get(x, "label")}
                            </DropdownItem>
                          ))}
                        </DropdownContent>
                      </DropdownContainer>
                    </div>
                  </div>
                </div>
              </div>

              <div className="stdntTabsCollect">
                <div className="stdntTabsLeft">
                  {Object.keys(semAssignment).map((key, index) => {
                    return (
                      <Tab
                        active={key == activeSem}
                        key={key}
                        onClick={() => {
                          setActiveSem(key);
                          setActiveSemData(semAssignment[key]);
                          // setActiveSemNotesLength(
                          //   semWiseNotes[key]?.length ?? 0
                          // );
                        }}
                      >
                        {key}
                      </Tab>
                    );
                  })}
                </div>
                <div
                  className="stdntTabsRght"
                  onClick={() => {
                    setOpenGradebook(!openGradebook);
                  }}
                >
                  {openGradebook ? (
                    <img
                      src={
                        tableViewSvg
                      }
                      alt=""
                    />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <g clip-path="url(#clip0_8_604)">
                        <path
                          d="M9.67 0C12.34 0 14.76 1.08 16.51 2.83C18.26 4.58 19.34 7 19.34 9.67C19.34 12.34 18.26 14.76 16.51 16.51C14.76 18.26 12.34 19.34 9.67 19.34C7 19.34 4.58 18.26 2.83 16.51C1.08 14.76 0 12.34 0 9.67C0 7 1.08 4.58 2.83 2.83C4.58 1.08 7 0 9.67 0Z"
                          fill="#E64C3C"
                        />
                        <path
                          d="M13.9802 13.33L10.0802 5.6C10.0402 5.51 9.97019 5.44 9.88019 5.39C9.65019 5.28 9.38019 5.37 9.27019 5.59L5.37019 13.32C5.26019 13.55 5.35019 13.82 5.57019 13.93C5.80019 14.04 6.07019 13.95 6.18019 13.73L7.85019 10.42H11.4902L13.1602 13.73C13.2702 13.96 13.5502 14.05 13.7702 13.93C14.0002 13.82 14.0802 13.54 13.9702 13.32L13.9802 13.33ZM8.31019 9.51L9.67019 6.81L11.0302 9.51H8.31019Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_8_604">
                          <rect width="19.34" height="19.34" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  )}
                  <label>
                    {openGradebook ? "View as table" : "View as gradebook"}
                  </label>
                </div>
              </div>

              {/* Student GradeBook  */}
              {openGradebook ? (
                <StudentGradebook
                  sectionSort={sectionSort}
                  filteredAssignments={filteredAssignments}
                  booksAllList={booksAllList}
                  fetchAssignmentSubmission={fetchAssignmentSubmission}
                />
              ) : (
                <WrapperSec1>
                  <SectionContainer>{renderGrades}</SectionContainer>
                </WrapperSec1>
              )}
              {/* End of GradeBook */}
            </Col>
            {/* Right Side Assignment Status */}
            <Col
              xs={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 7 }}
              className="quizCardSection"
            >
            
              <div className="quizCardSectionSet">
                <CardItem
                   title={<>Activities<br/>Completed</>}
                  value={assignmentInfo.activity.activityCompleted}
                  onClick={() => {
                    if (cardSelected === 'activity_completed') {
                      activeFilterRef.current = "all";
                      onselectionchange("all", bookSelection);
                      setCardSelected('');
                    } else {
                      activeFilterRef.current = "all";
                      onselectionchange("completed", bookSelection);
                      setCardSelected('activity_completed');
                    }
                  }}
                  max={assignmentInfo.activity.activityAssigned}
                  selected={cardSelected === 'activity_completed'}
                />
              </div>

              <div className="quizCardSectionSet">
                <CardItem
                  title={<>Activities<br/>Past due</>}
                  value={assignmentInfo.activity.pastDueActivity}
                  onClick={() => {
                    if (cardSelected === 'activity_past_due') {
                      activeFilterRef.current = "all";
                      onselectionchange("all", bookSelection);
                      setCardSelected('');
                    } else {
                      activeFilterRef.current = "activity_past_due";
                      onselectionchange("due_past", bookSelection);
                      setCardSelected('activity_past_due');
                    }
                  }}
                  max={assignmentInfo.activity.activityAssigned}
                  selected={cardSelected === 'activity_past_due'}
                />
              </div>

              <div className="quizCardSectionSet">
                <CardItem
                  title={"Examen\n completed"}
                  value={assignmentInfo.examin.examinCompleted}
                  onClick={() => {
                    if (cardSelected === 'Examen_complete') {
                      activeFilterRef.current = "all";
                      onselectionchange("all", bookSelection);
                      setCardSelected('');
                    } else {
                      activeFilterRef.current = "Examen_complete";
                      onselectionchange("completed", bookSelection);
                      setCardSelected('Examen_complete');
                    }
                  }}
                  max={assignmentInfo.examin.examinAssigned}
                  selected={cardSelected === 'Examen_complete'}
                />
              </div>

              <div className="quizCardSectionSet">
                <CardItem
                  title={"Examen\n past due"}  
                  value={assignmentInfo.examin.pastDueExamin}
                  onClick={() => {
                    if (cardSelected === 'examen_past') {
                      activeFilterRef.current = "all";
                      onselectionchange("all", bookSelection);
                      setCardSelected('');
                    } else {
                      activeFilterRef.current = "examen_past";
                      onselectionchange("due_past", bookSelection);
                      setCardSelected('examen_past');
                    }
                  }}
                  max={assignmentInfo.examin.examinAssigned}
                  selected={cardSelected === 'examen_past'}
                />
              </div>

              <div className="quizCardSectionSet">
                <CardItem
                  title={"Unit Exam\n completed"}
                  value={assignmentInfo.unitExamin.unitExaminCompleted}
                  onClick={() => {
                    if (cardSelected === 'unit_completed') {
                      activeFilterRef.current = "all";
                      onselectionchange("all", bookSelection);
                      setCardSelected('');
                    } else {
                      activeFilterRef.current = "unit_completed";
                      onselectionchange("completed", bookSelection);
                      setCardSelected('unit_completed');
                    }
                  }}
                  max={assignmentInfo.unitExamin.unitExaminAssigned}
                  selected={cardSelected === 'unit_completed'}
                />
              </div>

              <div className="quizCardSectionSet">
                <CardItem
                  title={"Unit Exam\n past due"}
                  value={assignmentInfo.unitExamin.pastDueUnitExamin}
                  onClick={() => {
                    if (cardSelected === 'unit_past') {
                      activeFilterRef.current = "all";
                      onselectionchange("all", bookSelection);
                      setCardSelected('');
                    } else {
                      activeFilterRef.current = "unit_past";
                      onselectionchange("due_past", bookSelection);
                      setCardSelected('unit_past');
                    }
                  }}
                  max={assignmentInfo.unitExamin.unitExaminAssigned}
                  selected={cardSelected === 'unit_past'}
                />
              </div>

              <div className="quizCardSectionSet daysApExam">
                <CardItem
                  title={"Days until exam"}
                  value={
                    <>
                      <span>
                        {Math.max(0, apExamLAN.diff(todayDate, "days"))} AP
                      </span>
                      <span>SPAN LAN</span>
                    </>
                  }
                  subValue={
                    <>
                      <span>
                        {Math.max(0, apExamLIT.diff(todayDate, "days"))} AP
                      </span>
                      <span>SPAN LIT</span>
                    </>
                  }
                  isAplang={true}
                  max={100}
                />
              </div>
            </Col>
          </Row>
        </>
      )}
      </Wrapper>
    </SentryWrap>
  );
};


const mapStateToProps = (state) => ({
  notes: state.notes,
  currentUser: state.currentUser,
  submissions: state.submissions,
  booksAllList: state.booksAllList,
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(forwardRef(ParentVisual));
